/**
 * Authentic Red Pen Annotation Renderer for HTML5 Canvas
 * Ports the exact mathematical algorithms, Bezier curves, and organic wobble
 * from AnnotationGrader.js to the frontend Canvas 2D API.
 * Supports interactive dragging and repositioning of all markings.
 */

// Authentic Teacher Red Pen Color & Palette (aligned directly with AnnotationGrader.js)
const RED_PEN_COLOR = "#d41c24"; // Authentic scarlet red ink (#d41c24)
const RED_PEN_OPACITY = 0.94;
const COMMENT_PEN_COLOR = "#e84038"; // Light red / coral ink (#e84038)
const COMMENT_PEN_OPACITY = 0.96;

export function pseudoRandom(s) {
  const x = Math.sin(s++) * 10000;
  return x - Math.floor(x);
}

/**
 * Calculates 36 points for an organic hand-drawn ellipse loop with natural wobble and overlap.
 */
export function getOrganicLoopPoints(cx, cy, rx, ry, seed = 1) {
  const startAngle = 0.75 + (pseudoRandom(seed) - 0.5) * 0.3; // ~45-50 degrees
  const overshoot = 0.95 + pseudoRandom(seed + 1) * 0.4; // 50-75 degrees overshoot
  const totalAngle = Math.PI * 2 + overshoot;
  const numSteps = 36;
  const stepAngle = totalAngle / numSteps;

  const tilt = -0.08 + (pseudoRandom(seed + 2) - 0.5) * 0.05; // -3 to -6 degrees human slant
  const cosTilt = Math.cos(tilt);
  const sinTilt = Math.sin(tilt);

  const points = [];
  for (let i = 0; i <= numSteps; i++) {
    const angle = startAngle + i * stepAngle;
    const progress = i / numSteps;
    const wobble1 = Math.sin(angle * 2 + seed) * 0.05;
    const wobble2 = Math.cos(angle * 3 + seed * 2) * 0.03;
    const currentRx = rx * (1 + wobble1);
    const currentRy = ry * (1 + wobble2);

    const spiralOffset = progress > 0.85 ? (progress - 0.85) * 4.5 : 0;
    const px = Math.cos(angle) * (currentRx + spiralOffset);
    const py = Math.sin(angle) * (currentRy + spiralOffset);

    const rotX = px * cosTilt - py * sinTilt;
    const rotY = px * sinTilt + py * cosTilt;

    points.push({ x: cx + rotX, y: cy + rotY });
  }
  return points;
}

/**
 * Wraps text into lines based on canvas measureText, matching AnnotationGrader.js wrapCommentText
 */
export function wrapCanvasCommentText(ctx, text, maxWidth) {
  if (!text) return [];
  const clean = String(text).trim();
  const rawParagraphs = clean.split(/\r?\n/).map((p) => p.trim()).filter(Boolean);
  const resultLines = [];

  for (const para of rawParagraphs) {
    const words = para.split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;

    let currentLine = "";
    for (const word of words) {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = ctx.measureText(candidate).width;
      if (testWidth <= maxWidth || !currentLine) {
        currentLine = candidate;
      } else {
        resultLines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) {
      resultLines.push(currentLine);
    }
  }
  return resultLines;
}

/**
 * Draws an imperfect hand-drawn loop with pressure modulation (firmer downstroke, lighter upstroke).
 */
export function drawHandDrawnLoop(ctx, { cx, cy, rx, ry, color = RED_PEN_COLOR, thickness = 2.0, seed = 1 }) {
  const points = getOrganicLoopPoints(cx, cy, rx, ry, seed);
  const n = points.length;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = RED_PEN_OPACITY;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (let i = 1; i < n; i++) {
    const progress = i / n;
    let segThickness = thickness;
    if (progress < 0.45) {
      segThickness = thickness * (1.0 + Math.sin(progress * Math.PI) * 0.28);
    } else if (progress < 0.8) {
      segThickness = thickness * 0.82;
    } else {
      const tailProgress = (progress - 0.8) / 0.2;
      segThickness = thickness * (0.82 - tailProgress * 0.35);
    }

    ctx.lineWidth = Math.max(0.8, segThickness);
    ctx.beginPath();
    ctx.moveTo(points[i - 1].x, points[i - 1].y);
    ctx.lineTo(points[i].x, points[i].y);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draws an organic division line between numerator and denominator.
 */
export function drawDivisionBar(ctx, { cx, cy, width, color = RED_PEN_COLOR, seed = 1 }) {
  const half = width / 2;
  const dip = -0.4 + (pseudoRandom(seed) - 0.5) * 1.0;
  const numSteps = 8;
  let prevPt = { x: cx - half - 2, y: cy };

  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = RED_PEN_OPACITY;
  ctx.lineCap = "round";

  for (let i = 1; i <= numSteps; i++) {
    const t = i / numSteps;
    const currX = cx - half - 2 + (width + 4) * t;
    const sag = Math.sin(t * Math.PI) * dip;
    const currY = cy + sag;
    const th = 1.6 + Math.sin(t * Math.PI) * 0.55;

    ctx.lineWidth = th;
    ctx.beginPath();
    ctx.moveTo(prevPt.x, prevPt.y);
    ctx.lineTo(currX, currY);
    ctx.stroke();
    prevPt = { x: currX, y: currY };
  }

  ctx.restore();
}

/**
 * Draws an authentic fluid red checkmark (tick) with wrist flick and pressure tapering.
 */
export function drawRedCheckmark(ctx, x, y, color = RED_PEN_COLOR, seed = 1, scale = 1.0) {
  const rnd = pseudoRandom(seed);
  const rnd2 = pseudoRandom(seed + 3);

  const downW = (4.5 + rnd * 1.4) * scale;
  const downH = (6.2 + rnd2 * 1.4) * scale;
  const upW = (12.5 + rnd * 2.8) * scale;
  const upH = (17 + rnd2 * 3.8) * scale;

  // In HTML5 Canvas, y increases downwards!
  const startPt = { x: x - downW, y: y - (2.5 + (rnd - 0.5)) * scale };
  const vertexPt = { x: x, y: y + downH };

  const strokeScale = Math.min(1.0, 0.65 + scale * 0.35);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = RED_PEN_OPACITY;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  let prev = startPt;
  for (let i = 1; i <= 4; i++) {
    const t = i / 4;
    const ctrlX = x - downW * 0.6;
    const ctrlY = y + downH * 0.3;
    const bx = (1 - t) * (1 - t) * startPt.x + 2 * (1 - t) * t * ctrlX + t * t * vertexPt.x;
    const by = (1 - t) * (1 - t) * startPt.y + 2 * (1 - t) * t * ctrlY + t * t * vertexPt.y;
    const th = (1.5 + t * 0.9) * strokeScale;

    ctx.lineWidth = Math.max(0.65, th);
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(bx, by);
    ctx.stroke();
    prev = { x: bx, y: by };
  }

  // Upward sweeping arc
  const endPt = { x: x + upW, y: y + downH - upH };
  const ctrlX = x + upW * 0.4;
  const ctrlY = y + downH - upH * 0.28;

  for (let i = 1; i <= 7; i++) {
    const t = i / 7;
    const bx = (1 - t) * (1 - t) * vertexPt.x + 2 * (1 - t) * t * ctrlX + t * t * endPt.x;
    const by = (1 - t) * (1 - t) * vertexPt.y + 2 * (1 - t) * t * ctrlY + t * t * endPt.y;
    const th = (2.4 - t * 1.5) * strokeScale;

    ctx.lineWidth = Math.max(0.6, th);
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(bx, by);
    ctx.stroke();
    prev = { x: bx, y: by };
  }

  ctx.restore();
}

/**
 * Draws authentic crossing hand strokes for a red crossmark (X).
 */
export function drawRedCrossmark(ctx, x, y, color = RED_PEN_COLOR, seed = 1, scale = 1.0) {
  const rnd1 = (pseudoRandom(seed) - 0.5) * 1.5 * scale;
  const rnd2 = (pseudoRandom(seed + 1) - 0.5) * 1.5 * scale;
  const s = 6.5 * scale;
  const strokeScale = Math.min(1.0, 0.65 + scale * 0.35);

  const th1 = Math.max(0.7, 2.3 * strokeScale);
  const th2 = Math.max(0.6, 1.8 * strokeScale);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = RED_PEN_OPACITY;
  ctx.lineCap = "round";

  // Stroke 1: Top-left to bottom-right
  ctx.lineWidth = th1;
  ctx.beginPath();
  ctx.moveTo(x - s + rnd1, y - s + rnd2);
  ctx.lineTo(x + s - rnd2, y + s - rnd1);
  ctx.stroke();

  // Stroke 2: Top-right to bottom-left
  ctx.lineWidth = th2;
  ctx.beginPath();
  ctx.moveTo(x + s + rnd1, y - s - rnd2);
  ctx.lineTo(x - s - rnd2, y + s + rnd1);
  ctx.stroke();

  ctx.restore();
}

/**
 * Draws question score in right margin inside a quick authentic single-stroke loop.
 */
export function drawQuestionScoreCircle(ctx, { x, y, score, color = RED_PEN_COLOR, seed = 1, scale = 1.0 }) {
  const scoreText = String(score);
  const fontSize = Math.round(15 * scale);
  const numChars = scoreText.length;
  const baseRx = numChars > 2 ? 16.5 + (numChars - 2) * 4.5 : 16.5;

  drawHandDrawnLoop(ctx, {
    cx: x,
    cy: y,
    rx: baseRx * scale,
    ry: 15.5 * scale,
    color,
    thickness: 1.8 * scale,
    seed,
  });

  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = RED_PEN_OPACITY;
  ctx.font = `bold ${fontSize}px 'Kalam', 'Patrick Hand', 'Comic Sans MS', cursive, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const slantAngle = ((-1.8 + (pseudoRandom(seed) - 0.5) * 1.5) * Math.PI) / 180;
  ctx.translate(x, y);
  ctx.rotate(slantAngle);
  ctx.fillText(scoreText, 0, 0);
  ctx.restore();
}

/**
 * Draws the top total score stamp on Page 1 in stacked fraction format (e.g. 13 / 20)
 * inside an organic hand-drawn loop. Aligned directly with AnnotationGrader.js drawTopScoreStamp.
 */
export function drawTopScoreStamp(ctx, { x, y, totalScore, maxScore, color = RED_PEN_COLOR, seed = 42, scale = 1.0 }) {
  const hasMax = maxScore !== undefined && maxScore !== null && Number(maxScore) > 0;
  const topText = String(totalScore);
  const btmText = hasMax ? String(maxScore) : "";

  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = RED_PEN_OPACITY;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  if (hasMax) {
    const topSize = Math.round((topText.length > 3 ? 18.5 : 20.5) * scale);
    const btmSize = Math.round((btmText.length > 3 ? 17.5 : 19.5) * scale);
    ctx.font = `bold ${topSize}px 'Kalam', 'Patrick Hand', 'Comic Sans MS', cursive, sans-serif`;
    const topW = ctx.measureText(topText).width;
    ctx.font = `bold ${btmSize}px 'Kalam', 'Patrick Hand', 'Comic Sans MS', cursive, sans-serif`;
    const btmW = ctx.measureText(btmText).width;
    const barW = Math.max(topW, btmW) + 12 * scale;

    const ovalRx = Math.max(barW / 2 + 10 * scale, 33.5 * scale);
    const ovalRy = 33.5 * scale;

    drawHandDrawnLoop(ctx, {
      cx: x,
      cy: y,
      rx: ovalRx,
      ry: ovalRy,
      color,
      thickness: 2.2 * scale,
      seed,
    });

    // Numerator with slight natural wrist tilt (-1.6 deg)
    ctx.save();
    ctx.font = `bold ${topSize}px 'Kalam', 'Patrick Hand', 'Comic Sans MS', cursive, sans-serif`;
    ctx.translate(x, y - 11.5 * scale);
    ctx.rotate((-1.6 * Math.PI) / 180);
    ctx.fillText(topText, 0, 0);
    ctx.restore();

    // Division bar between numerator and denominator
    drawDivisionBar(ctx, { cx: x, cy: y, width: barW, color, seed: seed + 5 });

    // Denominator with slight natural wrist tilt (-1.2 deg)
    ctx.save();
    ctx.font = `bold ${btmSize}px 'Kalam', 'Patrick Hand', 'Comic Sans MS', cursive, sans-serif`;
    ctx.translate(x, y + 12.5 * scale);
    ctx.rotate((-1.2 * Math.PI) / 180);
    ctx.fillText(btmText, 0, 0);
    ctx.restore();
  } else {
    const fontSize = Math.round((topText.length > 3 ? 19 : 22) * scale);
    const ovalR = 29 * scale;
    drawHandDrawnLoop(ctx, {
      cx: x,
      cy: y,
      rx: ovalR,
      ry: ovalR,
      color,
      thickness: 2.2 * scale,
      seed,
    });
    ctx.save();
    ctx.font = `bold ${fontSize}px 'Kalam', 'Patrick Hand', 'Comic Sans MS', cursive, sans-serif`;
    ctx.translate(x, y);
    ctx.rotate((-1.5 * Math.PI) / 180);
    ctx.fillText(topText, 0, 0);
    ctx.restore();
  }

  ctx.restore();
}

/**
 * Draws an organic hand-drawn horizontal line with subtle natural wobble.
 */
export function drawHandDrawnLine(ctx, { x1, y1, x2, y2, color = RED_PEN_COLOR, thickness = 2.0, seed = 1 }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  const numSteps = Math.max(6, Math.round(len / 16));

  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = RED_PEN_OPACITY;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  let prevPt = { x: x1, y: y1 };
  for (let i = 1; i <= numSteps; i++) {
    const t = i / numSteps;
    const baseProgressX = x1 + dx * t;
    const baseProgressY = y1 + dy * t;
    const sag = Math.sin(t * Math.PI) * (0.8 + (pseudoRandom(seed + i) - 0.5) * 1.5);
    const currX = baseProgressX;
    const currY = baseProgressY + sag;
    const segTh = thickness * (0.85 + Math.sin(t * Math.PI) * 0.3);

    ctx.lineWidth = Math.max(0.8, segTh);
    ctx.beginPath();
    ctx.moveTo(prevPt.x, prevPt.y);
    ctx.lineTo(currX, currY);
    ctx.stroke();
    prevPt = { x: currX, y: currY };
  }
  ctx.restore();
}

/**
 * Draws an authentic custom feedback comment matching backend AnnotationGrader.js renderCommentCard.
 * Transparent card with subtle red border (#e84038, borderWidth: 0.7) and handwritten font.
 */
export function drawCustomCommentBox(ctx, {
  x,
  y,
  text = "Feedback comment",
  color = COMMENT_PEN_COLOR,
  fontSize = 11,
  scale = 1.0,
  fontFamily = "handwritten",
  showPointer = false,
  pointerTargetX,
  pointerTargetY,
  canvasWidth,
  canvasHeight,
}) {
  ctx.save();
  const actualFontSize = Math.round(fontSize * scale);
  const fontFace = fontFamily === "sans"
    ? "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif"
    : "'Patrick Hand', 'Kalam', cursive, sans-serif";
  ctx.font = `bold ${actualFontSize}px ${fontFace}`;

  const cWidth = canvasWidth || ctx.canvas?.width || 1200;
  const cHeight = canvasHeight || ctx.canvas?.height || 1600;
  const rightMargin = 20 * scale;
  const leftMargin = 16 * scale;

  // Maximum wrapping width bounded by canvas width with margins
  const maxAllowedWrapW = Math.max(130 * scale, Math.min(290 * scale, cWidth - leftMargin - rightMargin));
  const lines = wrapCanvasCommentText(ctx, text, maxAllowedWrapW);
  const lineHeight = actualFontSize + 3.5 * scale;
  const maxLineW = Math.max(...lines.map((l) => ctx.measureText(l).width), 40 * scale);
  const blockH = Math.max(lines.length * lineHeight, 16 * scale);

  const padX = 7.0 * scale;
  const padY = 5.5 * scale;
  const cardW = maxLineW + 2 * padX;
  const cardH = blockH + 2 * padY;

  // Clamp draw position so the comment never bleeds off the right or bottom canvas margin
  let drawX = x;
  if (drawX + cardW > cWidth - rightMargin) {
    drawX = Math.max(leftMargin, cWidth - rightMargin - cardW);
  } else if (drawX < leftMargin) {
    drawX = leftMargin;
  }

  let drawY = y;
  if (cHeight && drawY + cardH > cHeight - 16 * scale) {
    drawY = Math.max(16 * scale, cHeight - 16 * scale - cardH);
  } else if (drawY < 16 * scale) {
    drawY = 16 * scale;
  }

  // Draw subtle card border matching AnnotationGrader.js renderCommentCard (borderWidth: 0.7, color: #e84038)
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(0.7, 0.75 * scale);
  ctx.globalAlpha = 0.90;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(drawX, drawY, cardW, cardH, 4 * scale);
    ctx.stroke();
  } else {
    ctx.strokeRect(drawX, drawY, cardW, cardH);
  }

  // Draw comment text lines in authentic teacher comment pen color
  ctx.fillStyle = color;
  ctx.globalAlpha = COMMENT_PEN_OPACITY;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  lines.forEach((line, lIdx) => {
    ctx.fillText(line, drawX + padX, drawY + padY + lIdx * lineHeight);
  });

  // Optional pointer line if target coordinate provided
  if (showPointer && pointerTargetX != null && pointerTargetY != null) {
    ctx.beginPath();
    const pointerStartX = pointerTargetX < drawX ? drawX : drawX + cardW;
    ctx.moveTo(pointerStartX, drawY + cardH / 2);
    ctx.lineTo(pointerTargetX, pointerTargetY);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2 * scale;
    ctx.setLineDash([3 * scale, 2 * scale]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();

  return { boxW: cardW, boxH: cardH, x: drawX, y: drawY };
}

/**
 * Computes bounding box and 4 corner coordinates (tl, tr, bl, br) for any mark.
 * Used for interactive corner handle resizing and floating toolbar placement.
 * Completely guarded against NaN so handles never collapse or disappear.
 */
export function getMarkingBounds(mark, canvas) {
  if (!mark) return null;
  const width = canvas?.width || 600;
  const scale = width / 600;

  const safeVal = (v, fallback = 0) => {
    const n = Number(v);
    return isNaN(n) || !isFinite(n) ? fallback : n;
  };

  let cx = safeVal(mark.x, 0);
  let cy = safeVal(mark.y, 0);
  let left = cx;
  let right = cx;
  let top = cy;
  let bottom = cy;

  if (mark.type === "top-score" || mark.markType === "top-score") {
    const stampScale = safeVal(mark.scale, 1.0);
    const r = safeVal(mark.rx, 36 * stampScale * scale);
    left = cx - r;
    right = cx + r;
    top = cy - r;
    bottom = cy + r;
  } else if (mark.haloShape && typeof mark.haloShape === "object") {
    if (mark.haloShape.type === "rect") {
      left = safeVal(mark.haloShape.x, cx - 20 * scale);
      top = safeVal(mark.haloShape.y, cy - 20 * scale);
      const w = safeVal(mark.haloShape.w, 40 * scale);
      const h = safeVal(mark.haloShape.h, 40 * scale);
      right = left + w;
      bottom = top + h;
      cx = (left + right) / 2;
      cy = (top + bottom) / 2;
    } else {
      const rx = safeVal(mark.haloShape.rx, safeVal(mark.rx, 20 * scale) + 4 * scale);
      const ry = safeVal(mark.haloShape.ry, safeVal(mark.ry, 16 * scale) + 4 * scale);
      left = cx - rx;
      right = cx + rx;
      top = cy - ry;
      bottom = cy + ry;
    }
  } else if (mark.rx && mark.ry) {
    const rx = safeVal(mark.rx, 20 * scale) + 4 * scale;
    const ry = safeVal(mark.ry, 14 * scale) + 4 * scale;
    left = cx - rx;
    right = cx + rx;
    top = cy - ry;
    bottom = cy + ry;
  } else if (mark.boxW && mark.boxH) {
    const w = safeVal(mark.boxW, 40 * scale);
    const h = safeVal(mark.boxH, 20 * scale);
    left = mark.x - 4 * scale;
    right = mark.x + w + 4 * scale;
    top = mark.y - 4 * scale;
    bottom = mark.y + h + 4 * scale;
    cx = (left + right) / 2;
    cy = (top + bottom) / 2;
  } else {
    const r = safeVal(mark.hitRadius, 22 * scale);
    left = cx - r;
    right = cx + r;
    top = cy - r;
    bottom = cy + r;
  }

  // Ensure no coordinates are NaN
  if (isNaN(cx)) cx = safeVal(mark.x, 0);
  if (isNaN(cy)) cy = safeVal(mark.y, 0);
  if (isNaN(left)) left = cx - 20 * scale;
  if (isNaN(right)) right = cx + 20 * scale;
  if (isNaN(top)) top = cy - 20 * scale;
  if (isNaN(bottom)) bottom = cy + 20 * scale;

  return {
    cx,
    cy,
    width: Math.max(20, right - left),
    height: Math.max(20, bottom - top),
    left,
    top,
    right,
    bottom,
    corners: {
      tl: { x: left, y: top, name: "tl", cursor: "nwse-resize", axis: "both" },
      tr: { x: right, y: top, name: "tr", cursor: "nesw-resize", axis: "both" },
      bl: { x: left, y: bottom, name: "bl", cursor: "nesw-resize", axis: "both" },
      br: { x: right, y: bottom, name: "br", cursor: "nwse-resize", axis: "both" },
      ml: { x: left, y: cy, name: "ml", cursor: "ew-resize", axis: "width" },
      mr: { x: right, y: cy, name: "mr", cursor: "ew-resize", axis: "width" },
      tc: { x: cx, y: top, name: "tc", cursor: "ns-resize", axis: "height" },
      bc: { x: cx, y: bottom, name: "bc", cursor: "ns-resize", axis: "height" },
    },
  };
}

/**
 * Draws an interactive selection / move indicator halo around an interactive marking.
 * Matches the exact shape and size of the annotation mark (circle, ellipse, or rect)
 * with circular corner drag handles for intuitive resizing.
 */
export function drawSelectionHalo(ctx, cx, cy, shapeOrRadius, isDragging, label, isSelected = false) {
  ctx.save();
  const strokeColor = isSelected ? "#2563eb" : (isDragging ? "#2563eb" : "rgba(59, 130, 246, 0.75)");
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = isSelected ? 2.0 : (isDragging ? 2.0 : 1.5);
  if (!isDragging && !isSelected) {
    ctx.setLineDash([4, 4]);
  } else {
    ctx.setLineDash([]);
  }
  ctx.fillStyle = isSelected
    ? "rgba(37, 99, 235, 0.10)"
    : (isDragging ? "rgba(37, 99, 235, 0.12)" : "rgba(59, 130, 246, 0.08)");

  let tagY = cy - 26;
  let bounds = null;

  if (typeof shapeOrRadius === "object" && shapeOrRadius !== null) {
    if (shapeOrRadius.type === "rect") {
      const { x, y, w, h } = shapeOrRadius;
      bounds = { left: x, top: y, right: x + w, bottom: y + h, cx: x + w / 2, cy: y + h / 2 };
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(x, y, w, h, 6);
      } else {
        ctx.rect(x, y, w, h);
      }
      ctx.fill();
      ctx.stroke();
      tagY = y - 24;
    } else {
      // Ellipse { rx, ry }
      const rx = Math.max(14, shapeOrRadius.rx || 20);
      const ry = Math.max(10, shapeOrRadius.ry || 14);
      bounds = { left: cx - rx, top: cy - ry, right: cx + rx, bottom: cy + ry, cx, cy };
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      tagY = cy - ry - 24;
    }
  } else {
    const radius = Math.max(14, Number(shapeOrRadius) || 20);
    bounds = { left: cx - radius, top: cy - radius, right: cx + radius, bottom: cy + radius, cx, cy };
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    tagY = cy - radius - 24;
  }

  // Draw circular handles if selected
  if (isSelected && bounds) {
    const cScale = (ctx.canvas?.width || 600) / 600;
    const handleRadius = Math.max(5.5, 4.5 * cScale);
    const minDim = Math.min(bounds.width, bounds.height);
    const isCircleShape =
      label?.toLowerCase().includes("circle") ||
      (typeof shapeOrRadius === "object" && shapeOrRadius !== null && shapeOrRadius?.type !== "rect" && Boolean(shapeOrRadius?.rx));

    const handles = [
      { x: bounds.left, y: bounds.top },
      { x: bounds.right, y: bounds.top },
      { x: bounds.left, y: bounds.bottom },
      { x: bounds.right, y: bounds.bottom },
    ];

    if (isCircleShape && minDim >= 50 * cScale) {
      handles.push(
        { x: bounds.left, y: bounds.cy },
        { x: bounds.right, y: bounds.cy },
        { x: bounds.cx, y: bounds.top },
        { x: bounds.cx, y: bounds.bottom }
      );
    }

    handles.forEach((pt) => {
      // White circle with vibrant blue border and subtle shadow
      ctx.save();
      ctx.shadowColor = "rgba(0, 0, 0, 0.25)";
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;

      ctx.beginPath();
      ctx.arc(pt.x, pt.y, handleRadius, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();

      ctx.shadowColor = "transparent";
      ctx.lineWidth = 2.0;
      ctx.strokeStyle = "#2563eb";
      ctx.setLineDash([]);
      ctx.stroke();
      ctx.restore();
    });
  }

  // Floating indicator tag
  if (isDragging || isSelected) {
    ctx.setLineDash([]);
    ctx.fillStyle = isDragging ? "#2563eb" : "#1d4ed8";
    const textStr = isDragging ? "Moving" : (label || "Selected");
    ctx.font = "bold 10.5px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    const textWidth = ctx.measureText(textStr).width;
    const tagW = Math.max(54, textWidth + 16);
    const tagH = 20;
    const tagX = cx - tagW / 2;

    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.20)";
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(tagX, tagY, tagW, tagH, 10);
    } else {
      ctx.rect(tagX, tagY, tagW, tagH);
    }
    ctx.fill();

    ctx.shadowColor = "transparent";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(textStr, cx, tagY + tagH / 2);
    ctx.restore();
  }

  ctx.restore();
}

/**
 * Hit test a canvas coordinate against all markings on a page.
 * Returns the hit marking object or null.
 */
export function hitTestMarking(markings, canvasX, canvasY) {
  if (!Array.isArray(markings) || markings.length === 0) return null;
  // Test in reverse order so topmost markings get priority
  for (let i = markings.length - 1; i >= 0; i--) {
    const mark = markings[i];
    if (mark.hitTest && typeof mark.hitTest === "function") {
      if (mark.hitTest(canvasX, canvasY)) {
        return mark;
      }
      continue;
    }
    // Also check haloShape or box if present
    if (mark.haloShape && typeof mark.haloShape === "object") {
      if (mark.haloShape.type === "rect") {
        const { x, y, w, h } = mark.haloShape;
        if (canvasX >= x - 4 && canvasX <= x + w + 4 && canvasY >= y - 4 && canvasY <= y + h + 4) {
          return mark;
        }
      } else if (mark.haloShape.rx && mark.haloShape.ry) {
        const dx = (canvasX - mark.x) / (mark.haloShape.rx + 4);
        const dy = (canvasY - mark.y) / (mark.haloShape.ry + 4);
        if (dx * dx + dy * dy <= 1.0) {
          return mark;
        }
      }
    }
    if (mark.boxW && mark.boxH) {
      if (
        canvasX >= mark.x - 6 &&
        canvasX <= mark.x + mark.boxW + 6 &&
        canvasY >= mark.y - 6 &&
        canvasY <= mark.y + mark.boxH + 6
      ) {
        return mark;
      }
    }
    const dx = canvasX - mark.x;
    const dy = canvasY - mark.y;
    const distSq = dx * dx + dy * dy;
    const hitR = mark.hitRadius || 24;
    if (distSq <= hitR * hitR) {
      return mark;
    }
  }
  return null;
}

/**
 * Main Frontend Red Pen Annotation Dispatcher:
 * Computes and renders all red pen markings for target pageNum onto the canvas.
 * Supports custom positions (for interactive dragging/moving), hover/drag halos,
 * and returns the list of marking descriptors for pointer hit-testing.
 */
export function renderRedPenAnnotationsToCanvas(
  canvas,
  pageNum,
  scoreData,
  customPositions = {},
  options = {}
) {
  if (!canvas || !scoreData || !Array.isArray(scoreData.result)) return [];

  const ctx = canvas.getContext("2d");
  const width = canvas.width;
  const height = canvas.height;
  if (!width || !height) return [];

  if (options.clearCanvas) {
    ctx.clearRect(0, 0, width, height);
  }

  // Scale factor relative to standard 600x850 page
  const scale = width / 600;

  // Compute total obtained and max score
  let totalScore = 0;
  let maxScore = 0;
  scoreData.result.forEach((q, qIdx) => {
    const qNumRaw = q.questionNumber || `${qIdx + 1}`;
    const qKey = String(qNumRaw).trim();
    const customScorePos =
      customPositions[`q-score-${qKey}`] ||
      customPositions[`q-score-q${qKey}`] ||
      customPositions[`q-score-${qNumRaw}`] ||
      customPositions[`q-score-${qIdx + 1}`] ||
      (q._id && customPositions[`q-score-${q._id}`]);
    const obtained =
      customScorePos?.score !== undefined && customScorePos.score !== null
        ? Number(customScorePos.score)
        : (q.obtainedScore ?? 0);

    totalScore += obtained;
    maxScore += q.maxScore || 0;
  });

  const markings = [];

  // 1. Top Header Score Stamp (Page 1)
  if (pageNum === 1) {
    const stampId = "top-score-stamp";
    const topScoreDoc = scoreData.topScorePosition;
    const customPos = customPositions[stampId];
    const isStampDeleted = customPos?.deleted !== undefined ? customPos.deleted : Boolean(topScoreDoc?.deleted);

    if (!isStampDeleted) {
      const defaultStampX = width - 62 * scale;
      const defaultStampY = 82 * scale;
      const stampScale = customPos?.scale || topScoreDoc?.scale || 1.0;
      const mScale = stampScale * scale * 0.95;
      const mColor = customPos?.color || RED_PEN_COLOR;
      const stampX = customPos?.xRatio != null
        ? customPos.xRatio * width
        : (topScoreDoc?.customX != null ? topScoreDoc.customX * width : defaultStampX);
      const stampY = customPos?.yRatio != null
        ? customPos.yRatio * height
        : (topScoreDoc?.customY != null ? topScoreDoc.customY * height : defaultStampY);
      const ovalRx = 36 * stampScale * scale;
      const ovalRy = 36 * stampScale * scale;
      const haloShape = { type: "ellipse", rx: ovalRx + 4 * scale, ry: ovalRy + 4 * scale };
      const hitRadius = ovalRx + 6 * scale;

      markings.push({
        id: stampId,
        type: "top-score",
        markType: "top-score",
        pageNum: 1,
        x: stampX,
        y: stampY,
        xRatio: +(stampX / width).toFixed(4),
        yRatio: +(stampY / height).toFixed(4),
        rx: ovalRx,
        ry: ovalRy,
        haloShape,
        defaultX: defaultStampX,
        defaultY: defaultStampY,
        scale: stampScale,
        color: mColor,
        hitRadius,
        label: "Total Score Stamp",
        draw: (c, isHov, isDrag, isSel) => {
          drawTopScoreStamp(c, {
            x: stampX,
            y: stampY,
            totalScore,
            maxScore,
            color: mColor,
            seed: 42,
            scale: mScale,
          });
          if (options.interactive && (isHov || isDrag || isSel)) {
            drawSelectionHalo(c, stampX, stampY, haloShape, isDrag, "Total Score", isSel);
          }
        },
      });
    }
  }

  // 2. Question Scores & Step Marks
  const questionScoresToDraw = [];
  let markSeedCounter = (pageNum * 79) % 1000;
  const renderedMarkYs = [];
  const isLineMarked = (y) => renderedMarkYs.some((prevY) => Math.abs(prevY - y) < 14 * scale);
  const recordLineMark = (y) => renderedMarkYs.push(y);
  const renderedCommentBoxes = [];

  scoreData.result.forEach((question, qIdx) => {
    const qNumRaw = question.questionNumber || (qIdx + 1);
    const qKey = String(qNumRaw).trim();
    const customScorePos =
      customPositions[`q-score-${qKey}`] ||
      customPositions[`q-score-q${qKey}`] ||
      customPositions[`q-score-${qNumRaw}`] ||
      customPositions[`q-score-${qIdx + 1}`] ||
      (question._id && customPositions[`q-score-${question._id}`]);
    const obtained =
      customScorePos?.score !== undefined && customScorePos.score !== null
        ? Number(customScorePos.score)
        : (question.obtainedScore ?? 0);

    // Skip unattempted questions completely (matching AnnotationGrader.js)
    const isUnattempted =
      question.studentAnswer === "NOT ATTEMPTED" ||
      question.isNotAttempted === true ||
      (obtained === 0 && !question.scorePosition && (!question.stepEvaluations || !question.stepEvaluations.length));

    if (isUnattempted) {
      return;
    }

    // Steps & annotations on this page
    const pageSteps = (question.stepEvaluations || []).filter(
      (s) => Number(s.pageNumber || 1) === pageNum &&
        ((s.boundingBox && s.boundingBox.length === 4) || (s.customX != null && s.customY != null))
    );
    const pageAnns = (question.annotations || []).filter(
      (a) => Number(a.pageNumber || 1) === pageNum &&
        ((a.boundingBox && a.boundingBox.length === 4) || (a.customX != null && a.customY != null) || (a.commentCustomX != null && a.commentCustomY != null))
    );

    // On Page 1, filter out any spurious AI steps/annotations inside the header / student info region (ymin < 150)
    const validPageSteps = pageSteps.filter(
      (s) => !(pageNum === 1 && !s.isCustom && s.boundingBox && s.boundingBox[0] < 150)
    );
    const validPageAnns = pageAnns.filter(
      (a) => !(pageNum === 1 && !a.isCustom && a.boundingBox && a.boundingBox[0] < 150)
    );

    const qScorePos = question.scorePosition;
    const hasValidScorePosOnPage =
      Number(qScorePos?.pageNumber || 1) === pageNum &&
      qScorePos?.boundingBox &&
      qScorePos.boundingBox.length === 4 &&
      !(pageNum === 1 && qScorePos.boundingBox[0] < 150);

    const customQPos =
      customPositions[`q-score-${qKey}`] ||
      customPositions[`q-score-${qNumRaw}`] ||
      customPositions[`q-score-${qIdx + 1}`] ||
      (question._id && customPositions[`q-score-${question._id}`]) ||
      (qScorePos?.customX != null && qScorePos?.customY != null
        ? {
            xRatio: qScorePos.customX,
            yRatio: qScorePos.customY,
            pageNum: qScorePos.pageNumber || 1,
            scale: qScorePos.scale,
            deleted: qScorePos.deleted,
          }
        : null);

    const isQScoreDeleted = Boolean(customQPos?.deleted ?? qScorePos?.deleted);

    const targetPage =
      customQPos?.pageNum ||
      (qScorePos?.pageNumber ? Number(qScorePos.pageNumber) : null) ||
      (validPageSteps.length > 0 ? pageNum : null) ||
      (validPageAnns.length > 0 ? pageNum : null);

    if (pageNum === targetPage && !isQScoreDeleted) {
      let bestScoreBox = null;
      if (hasValidScorePosOnPage) {
        bestScoreBox = qScorePos.boundingBox;
      } else {
        const candidateBoxes = [
          ...validPageSteps.filter(s => s.boundingBox && s.boundingBox.length === 4).map((s) => s.boundingBox),
          ...validPageAnns.filter(a => a.boundingBox && a.boundingBox.length === 4).map((a) => a.boundingBox),
        ];
        if (candidateBoxes.length > 0) {
          bestScoreBox = candidateBoxes[0];
        }
      }

      let idealY;
      if (customQPos?.yRatio != null) {
        idealY = customQPos.yRatio * height;
      } else if (qScorePos?.customY != null) {
        idealY = qScorePos.customY * height;
      } else if (bestScoreBox) {
        const lineCenterNormY = (bestScoreBox[0] + bestScoreBox[2]) / 2;
        idealY = (lineCenterNormY / 1000) * height;
      } else {
        idealY = (0.2 + (qIdx * 0.15)) * height;
      }

      questionScoresToDraw.push({
        qNum: qNumRaw,
        qKey,
        score: obtained,
        maxScore: question.maxScore ?? 1,
        questionId: question._id,
        question,
        idealY,
        qIdx,
        customQPos,
        qScorePos,
      });
    }

    // Render circled errors and feedback comments
    validPageAnns.forEach((ann, aIdx) => {
      markSeedCounter += 11;
      const loopId = ann._id ? `ann-loop-${ann._id}` : `ann-loop-${qKey}-${aIdx}`;
      const customPos =
        customPositions[loopId] ||
        (ann._id && customPositions[`ann-loop-${ann._id}`]) ||
        customPositions[`ann-loop-${qKey}-${aIdx}`] ||
        customPositions[`ann-loop-q${qKey}-a${aIdx}`] ||
        customPositions[`ann-loop-${qIdx + 1}-${aIdx}`];

      let defaultCenterX, defaultCenterY, boxW, boxH;
      if (ann.boundingBox && ann.boundingBox.length === 4) {
        const [ymin, xmin, ymax, xmax] = ann.boundingBox;
        const boxX = (xmin / 1000) * width;
        const boxY = (ymin / 1000) * height;
        boxW = ((xmax - xmin) / 1000) * width;
        boxH = ((ymax - ymin) / 1000) * height;
        defaultCenterX = boxX + boxW / 2;
        defaultCenterY = boxY + boxH / 2;
      } else {
        boxW = 40 * scale;
        boxH = 20 * scale;
        defaultCenterX = (ann.customX ?? ann.commentCustomX ?? 0.5) * width;
        defaultCenterY = (ann.customY ?? ann.commentCustomY ?? 0.5) * height;
      }

      const mScale = customPos?.scale || ann.scale || 1.0;
      const mColor = customPos?.color || RED_PEN_COLOR;
      const defaultRx = Math.max(boxW / 2 + 6 * scale, 16 * scale);
      const defaultRy = Math.min(Math.max(boxH / 2 + 3 * scale, 10 * scale), 14 * scale);
      const customRadiusRatio = customPos?.radiusRatio ?? ann.radiusRatio;
      const customHeightRatio = customPos?.heightRatio ?? ann.heightRatio;
      const xRadius = (customRadiusRatio != null ? customRadiusRatio * width : defaultRx) * mScale;
      const yRadius = (customHeightRatio != null ? (customHeightRatio * height) / 2 : defaultRy) * mScale;

      const centerX = customPos?.xRatio != null
        ? customPos.xRatio * width
        : (ann.customX != null ? ann.customX * width : defaultCenterX);
      const centerY = customPos?.yRatio != null
        ? customPos.yRatio * height
        : (ann.customY != null ? ann.customY * height : defaultCenterY);

      const hasCircle = ann.circleLine !== false;
      const hasUnderline = ann.underline === true;
      const isLoopDeleted = Boolean(customPos?.deleted ?? ann.deleted);

      // Smart placement, word-wrapping, and collision cascading (matching AnnotationGrader.js)
      const rawComment = ann.comment || ann.suggestion;
      const commentId = ann._id ? `ann-comment-${ann._id}` : `ann-comment-${qKey}-${aIdx}`;
      const customCommentPos =
        customPositions[commentId] ||
        (ann._id && customPositions[`ann-comment-${ann._id}`]) ||
        (!ann.isCustom ? (
          customPositions[`ann-comment-${qKey}-${aIdx}`] ||
          customPositions[`ann-comment-q${qKey}-a${aIdx}`] ||
          customPositions[`ann-comment-${qIdx + 1}-${aIdx}`]
        ) : null);

      const isCommentDeleted = Boolean(customCommentPos?.deleted ?? (ann.deletedComment || ann.deleted));
      const comment = customCommentPos?.text !== undefined
        ? customCommentPos.text
        : (customPos?.text !== undefined ? customPos.text : (ann.comment !== undefined ? ann.comment : rawComment));

      // Only render feedback comment if this annotation actually has a comment,
      // or if it's a custom note with comment text
      const hasActualComment = Boolean(
        (ann.isCustom && comment) ||
        rawComment ||
        (ann._id && customPositions[`ann-comment-${ann._id}`]?.text)
      );

      let commentLayout = null;

      if (hasActualComment && comment && !isCommentDeleted) {
        const cScale = customCommentPos?.scale || mScale;
        const fontSize = Math.round((customCommentPos?.fontSize || 10.5) * scale * cScale);
        const lineHeight = fontSize + 3.5 * scale;
        ctx.save();
        const cFontFamily = customCommentPos?.fontFamily || "sans";
        const fontFace = cFontFamily === "handwritten"
          ? "'Patrick Hand', 'Kalam', cursive, sans-serif"
          : "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif";
        ctx.font = `bold ${fontSize}px ${fontFace}`;

        let placement = ann.placement || "above";

        // 1. Right placement
        if (placement === "right") {
          const minTextX = Math.min(width - 160 * scale, centerX + xRadius + 14 * scale);
          const maxRightX = width - 40 * scale;
          const availableWidth = maxRightX - minTextX;

          if (availableWidth >= 80 * scale) {
            const lines = wrapCanvasCommentText(ctx, comment, availableWidth);
            const maxTextW = Math.max(...lines.map((l) => ctx.measureText(l).width), 60 * scale);
            const blockH = lines.length * lineHeight;
            let textY = centerY - blockH / 2;

            for (const prev of renderedCommentBoxes) {
              const xOverlap = Math.max(0, Math.min(minTextX + maxTextW, prev.x + prev.w) - Math.max(minTextX, prev.x));
              if (xOverlap > 10 * scale && Math.abs(prev.y - textY) < blockH + 6 * scale) {
                textY = prev.y + prev.h + 5 * scale;
              }
            }

            commentLayout = {
              placement: "right",
              lines,
              textX: minTextX,
              textY,
              maxTextW,
              blockH,
              fontSize: customCommentPos?.fontSize || 10.5,
              lineHeight,
              pointerStartX: minTextX - 4 * scale,
              pointerStartY: centerY,
              pointerTargetX: centerX + xRadius + 2 * scale,
              pointerTargetY: centerY,
            };
          } else {
            placement = centerY < 70 * scale ? "below" : "above";
          }
        }

        // 2. Below placement
        if (placement === "below" || (placement !== "right" && centerY < 70 * scale)) {
          const maxWrapW = Math.min(290 * scale, width - 60 * scale);
          const lines = wrapCanvasCommentText(ctx, comment, maxWrapW);
          const maxTextW = Math.max(...lines.map((l) => ctx.measureText(l).width), 60 * scale);
          const blockH = lines.length * lineHeight;
          const textX = Math.max(25 * scale, Math.min(width - maxTextW - 25 * scale, centerX - maxTextW / 2));
          let textY = centerY + (hasCircle ? yRadius + 6 * scale : boxH / 2 + 6 * scale);

          for (const prev of renderedCommentBoxes) {
            const xOverlap = Math.max(0, Math.min(textX + maxTextW, prev.x + prev.w) - Math.max(textX, prev.x));
            if (xOverlap > 10 * scale && Math.abs(prev.y - textY) < blockH + 6 * scale) {
              textY = prev.y + prev.h + 5 * scale;
            }
          }

          commentLayout = {
            placement: "below",
            lines,
            textX,
            textY,
            maxTextW,
            blockH,
            fontSize: customCommentPos?.fontSize || 10.5,
            lineHeight,
          };
        }
        // 3. Above placement
        else if (placement === "above") {
          const maxWrapW = Math.min(290 * scale, width - 60 * scale);
          const lines = wrapCanvasCommentText(ctx, comment, maxWrapW);
          const maxTextW = Math.max(...lines.map((l) => ctx.measureText(l).width), 60 * scale);
          const blockH = lines.length * lineHeight;
          const textX = Math.max(25 * scale, Math.min(width - maxTextW - 25 * scale, centerX - maxTextW / 2));
          let textY = centerY - (hasCircle ? yRadius + blockH + 6 * scale : boxH / 2 + blockH + 6 * scale);

          for (const prev of renderedCommentBoxes) {
            const xOverlap = Math.max(0, Math.min(textX + maxTextW, prev.x + prev.w) - Math.max(textX, prev.x));
            if (xOverlap > 10 * scale && Math.abs(prev.y - textY) < blockH + 6 * scale) {
              textY = prev.y - blockH - 5 * scale;
            }
          }

          commentLayout = {
            placement: "above",
            lines,
            textX,
            textY,
            maxTextW,
            blockH,
            fontSize: customCommentPos?.fontSize || 10.5,
            lineHeight,
          };
        }

        ctx.restore();

        if (commentLayout) {
          const finalCX = customCommentPos?.xRatio != null ? customCommentPos.xRatio * width : commentLayout.textX;
          const finalCY = customCommentPos?.yRatio != null ? customCommentPos.yRatio * height : commentLayout.textY;
          renderedCommentBoxes.push({ x: finalCX, y: finalCY, w: commentLayout.maxTextW, h: commentLayout.blockH });
        }
      }

      const currentBoxX = centerX - (boxW * mScale) / 2;
      const currentBoxY = centerY - (boxH * mScale) / 2;
      const isUnderlineOnly = hasUnderline && !hasCircle;

      if ((hasCircle || hasUnderline) && !isLoopDeleted) {
        const haloShape = isUnderlineOnly
          ? {
              type: "rect",
              x: currentBoxX - 4 * scale,
              y: currentBoxY + boxH * mScale - 3 * scale,
              w: boxW * mScale + 8 * scale,
              h: 9 * scale,
            }
          : {
              type: "ellipse",
              rx: xRadius + 5 * scale,
              ry: yRadius + 4 * scale,
            };

        const hitTest = (hx, hy) => {
          if (isUnderlineOnly) {
            if (
              hx >= currentBoxX - 8 * scale &&
              hx <= currentBoxX + boxW * mScale + 8 * scale &&
              hy >= currentBoxY + boxH * mScale - 8 * scale &&
              hy <= currentBoxY + boxH * mScale + 12 * scale
            ) {
              return true;
            }
          } else {
            const normX = (hx - centerX) / (xRadius + 6 * scale);
            const normY = (hy - centerY) / (yRadius + 5 * scale);
            if (normX * normX + normY * normY <= 1.0) {
              return true;
            }
          }
          return false;
        };

        markings.push({
          id: loopId,
          type: "ann-loop",
          markType: "ann-loop",
          pageNum,
          questionIndex: qIdx,
          qIdx,
          qNum: qNumRaw,
          qKey,
          questionId: question._id,
          annotationIndex: aIdx,
          annotationId: ann._id,
          x: centerX,
          y: centerY,
          xRatio: +(centerX / width).toFixed(4),
          yRatio: +(centerY / height).toFixed(4),
          rx: xRadius,
          ry: yRadius,
          haloShape,
          boxW: boxW * mScale,
          boxH: boxH * mScale,
          defaultX: defaultCenterX,
          defaultY: defaultCenterY,
          scale: mScale,
          color: mColor,
          strokeWidth: customPos?.strokeWidth || ann.strokeWidth || 1.8,
          hitRadius: Math.max(xRadius + 6 * scale, 24 * scale),
          hitTest,
          label: isUnderlineOnly ? "Underline Annotation" : "Error Circle",
          draw: (c, isHov, isDrag, isSel) => {
            if (hasCircle) {
              drawHandDrawnLoop(c, {
                cx: centerX,
                cy: centerY,
                rx: xRadius,
                ry: yRadius,
                color: mColor,
                thickness: (customPos?.strokeWidth || 1.8) * scale * mScale,
                seed: markSeedCounter,
              });
            }

            if (hasUnderline) {
              c.save();
              c.beginPath();
              c.moveTo(currentBoxX, currentBoxY + boxH * mScale + 2 * scale);
              c.lineTo(currentBoxX + boxW * mScale, currentBoxY + boxH * mScale + 2 * scale);
              c.strokeStyle = mColor;
              c.lineWidth = 1.6 * scale * mScale;
              c.stroke();
              c.restore();
            }

            if (options.interactive && (isHov || isDrag || isSel)) {
              drawSelectionHalo(c, centerX, centerY, haloShape, isDrag, isUnderlineOnly ? "Underline" : "Circle", isSel);
            }
          },
        });
      }

      // Render Feedback Comment as its own selectable, movable marking
      if (comment && !isCommentDeleted && commentLayout) {
        const cScale = customCommentPos?.scale || 1.0;
        const cColor = customCommentPos?.color || mColor;
        const cFontSize = customCommentPos?.fontSize || 10.5;
        const cFontFamily = customCommentPos?.fontFamily || "sans";

        const commentX = customCommentPos?.xRatio != null
          ? customCommentPos.xRatio * width
          : (ann.commentCustomX != null
              ? ann.commentCustomX * width
              : commentLayout.textX);
        const commentY = customCommentPos?.yRatio != null
          ? customCommentPos.yRatio * height
          : (ann.commentCustomY != null
              ? ann.commentCustomY * height
              : commentLayout.textY);

        let renderedCommentBox = { boxW: commentLayout.maxTextW, boxH: commentLayout.blockH, x: commentX, y: commentY };

        const commentHitTest = (hx, hy) => {
          const w = renderedCommentBox.boxW;
          const h = renderedCommentBox.boxH;
          const curX = renderedCommentBox.x ?? commentX;
          const curY = renderedCommentBox.y ?? commentY;
          return (
            hx >= curX - 6 * scale &&
            hx <= curX + w + 6 * scale &&
            hy >= curY - 6 * scale &&
            hy <= curY + h + 6 * scale
          );
        };

        markings.push({
          id: commentId,
          type: "comment",
          markType: "comment",
          pageNum,
          questionIndex: qIdx,
          qIdx,
          qNum: qNumRaw,
          qKey,
          questionId: question._id,
          annotationIndex: aIdx,
          annotationId: ann._id,
          x: commentX,
          y: commentY,
          xRatio: +(commentX / width).toFixed(4),
          yRatio: +(commentY / height).toFixed(4),
          boxW: renderedCommentBox.boxW,
          boxH: renderedCommentBox.boxH,
          haloShape: {
            type: "rect",
            x: commentX - 4 * scale,
            y: commentY - 4 * scale,
            w: renderedCommentBox.boxW + 8 * scale,
            h: renderedCommentBox.boxH + 8 * scale,
          },
          defaultX: commentLayout.textX,
          defaultY: commentLayout.textY,
          scale: cScale,
          color: cColor,
          text: comment,
          fontSize: cFontSize,
          fontFamily: cFontFamily,
          hitRadius: Math.max(renderedCommentBox.boxW / 2, 26 * scale),
          hitTest: commentHitTest,
          label: "Comment",
          draw: (c, isHov, isDrag, isSel) => {
            const res = drawCustomCommentBox(c, {
              x: commentX,
              y: commentY,
              text: comment,
              color: cColor,
              fontSize: cFontSize,
              scale: scale * cScale,
              fontFamily: cFontFamily,
              showPointer: customCommentPos?.showPointer === true || (commentLayout.placement === "right" && customCommentPos?.showPointer !== false && hasCircle),
              pointerTargetX: centerX + xRadius + 2 * scale,
              pointerTargetY: centerY,
              canvasWidth: width,
              canvasHeight: height,
            });
            if (res) renderedCommentBox = res;

            const haloX = renderedCommentBox.x ?? commentX;
            const haloY = renderedCommentBox.y ?? commentY;

            if (options.interactive && (isHov || isDrag || isSel)) {
              drawSelectionHalo(
                c,
                haloX + renderedCommentBox.boxW / 2,
                haloY + renderedCommentBox.boxH / 2,
                {
                  type: "rect",
                  x: haloX - 4 * scale,
                  y: haloY - 4 * scale,
                  w: renderedCommentBox.boxW + 8 * scale,
                  h: renderedCommentBox.boxH + 8 * scale,
                },
                isDrag,
                "Comment",
                isSel
              );
            }
          },
        });
      }
    });

    // Render checkmarks / crosses from stepEvaluations
    if (validPageSteps.length > 0) {
      validPageSteps.forEach((step, sIdx) => {
        markSeedCounter += 7;
        const stepId = step._id ? `step-mark-${step._id}` : `step-mark-${qKey}-${sIdx}`;
        const customPos =
          customPositions[stepId] ||
          (step._id && customPositions[`step-mark-${step._id}`]) ||
          customPositions[`step-mark-${qKey}-${sIdx}`] ||
          customPositions[`step-mark-q${qKey}-s${sIdx}`] ||
          customPositions[`step-mark-${qIdx + 1}-${sIdx}`];

        const isStepDeleted = Boolean(customPos?.deleted ?? step.deleted);
        if (isStepDeleted) {
          return;
        }

        let defaultStepX, defaultStepY;
        if (step.boundingBox && step.boundingBox.length === 4) {
          const [ymin, , ymax, xmax] = step.boundingBox;
          defaultStepX = Math.min(width - 68 * scale, (xmax / 1000) * width + 10 * scale);
          defaultStepY = ((ymin + ymax) / 2000) * height;
        } else {
          defaultStepX = (step.customX ?? 0.85) * width;
          defaultStepY = (step.customY ?? 0.5) * height;
        }

        if (!isLineMarked(defaultStepY) || step.customX != null || customPos?.xRatio != null) {
          recordLineMark(defaultStepY);
          const mScale = customPos?.scale || step.scale || 1.0;
          const mColor = customPos?.color || RED_PEN_COLOR;
          const stepX = customPos?.xRatio != null
            ? customPos.xRatio * width
            : (step.customX != null ? step.customX * width : defaultStepX);
          const stepY = customPos?.yRatio != null
            ? customPos.yRatio * height
            : (step.customY != null ? step.customY * height : defaultStepY);
          const hitRadius = 22 * scale * mScale;
          const currentSeed = markSeedCounter;

          const markHaloR = 18 * scale * mScale;
          markings.push({
            id: stepId,
            type: "step-mark",
            markType: "step-mark",
            pageNum,
            questionIndex: qIdx,
            qIdx,
            qNum: qNumRaw,
            qKey,
            questionId: question._id,
            stepIndex: sIdx,
            stepId: step._id,
            status: step.status,
            x: stepX,
            y: stepY,
            xRatio: +(stepX / width).toFixed(4),
            yRatio: +(stepY / height).toFixed(4),
            rx: markHaloR,
            ry: markHaloR,
            haloShape: { type: "ellipse", rx: markHaloR, ry: markHaloR },
            defaultX: defaultStepX,
            defaultY: defaultStepY,
            scale: mScale,
            color: mColor,
            hitRadius,
            label: step.status === "correct" ? "Checkmark" : "Crossmark",
            draw: (c, isHov, isDrag, isSel) => {
              if (step.status === "correct") {
                drawRedCheckmark(c, stepX, stepY, mColor, currentSeed, scale * 0.95 * mScale);
              } else if (step.status === "incorrect") {
                drawRedCrossmark(c, stepX, stepY, mColor, currentSeed, scale * 0.95 * mScale);
              }
              if (options.interactive && (isHov || isDrag || isSel)) {
                drawSelectionHalo(c, stepX, stepY, markHaloR, isDrag, step.status === "correct" ? "Checkmark" : "Crossmark", isSel);
              }
            },
          });
        }
      });
    } else if (validPageAnns.length > 0) {
      validPageAnns.forEach((ann, aIdx) => {
        if (ann.annotationType === "tick" || ann.annotationType === "check") {
          const [ymin, , ymax, xmax] = ann.boundingBox;
          const defaultMarkX = Math.min(width - 68 * scale, (xmax / 1000) * width + 12 * scale);
          const defaultMarkY = ((ymin + ymax) / 2000) * height;

          if (!isLineMarked(defaultMarkY)) {
            recordLineMark(defaultMarkY);
            const annMarkId = ann._id ? `ann-mark-${ann._id}` : `ann-mark-${qKey}-${aIdx}`;
            const customPos =
              customPositions[annMarkId] ||
              (ann._id && customPositions[`ann-mark-${ann._id}`]) ||
              customPositions[`ann-mark-${qKey}-${aIdx}`] ||
              customPositions[`ann-mark-q${qKey}-a${aIdx}`] ||
              customPositions[`ann-mark-${qIdx + 1}-${aIdx}`];

            if (customPos?.deleted) {
              return;
            }

            const mScale = customPos?.scale || 1.0;
            const mColor = customPos?.color || RED_PEN_COLOR;
            const markX = customPos ? customPos.xRatio * width : defaultMarkX;
            const markY = customPos ? customPos.yRatio * height : defaultMarkY;
            const hitRadius = 22 * scale * mScale;
            const currentSeed = markSeedCounter;
            const annHaloR = 18 * scale * mScale;

            markings.push({
              id: annMarkId,
              type: "ann-mark",
              pageNum,
              x: markX,
              y: markY,
              rx: annHaloR,
              ry: annHaloR,
              haloShape: { type: "ellipse", rx: annHaloR, ry: annHaloR },
              defaultX: defaultMarkX,
              defaultY: defaultMarkY,
              scale: mScale,
              color: mColor,
              hitRadius,
              label: "Checkmark",
              draw: (c, isHov, isDrag, isSel) => {
                drawRedCheckmark(c, markX, markY, mColor, currentSeed, scale * 0.95 * mScale);
                if (options.interactive && (isHov || isDrag || isSel)) {
                  drawSelectionHalo(c, markX, markY, annHaloR, isDrag, "Checkmark", isSel);
                }
              },
            });
          }
        }
      });
    }
  });

  // 3. Question Score Circles in Right Margin (Vertical overlap resolved)
  questionScoresToDraw.sort((a, b) => a.idealY - b.idealY);
  for (let j = 1; j < questionScoresToDraw.length; j++) {
    if (questionScoresToDraw[j].idealY - questionScoresToDraw[j - 1].idealY < 32 * scale) {
      questionScoresToDraw[j].idealY = questionScoresToDraw[j - 1].idealY + 32 * scale;
    }
  }

  questionScoresToDraw.forEach((qs, idx) => {
    const qScoreId = `q-score-${qs.qKey || qs.qNum || (idx + 1)}`;
    const defaultScoreX = width - 36 * scale;
    const defaultScoreY = qs.idealY;
    const customPos =
      customPositions[qScoreId] ||
      customPositions[`q-score-${qs.qKey}`] ||
      customPositions[`q-score-q${qs.qKey}`] ||
      customPositions[`q-score-${String(qs.qNum).trim()}`] ||
      customPositions[`q-score-q${String(qs.qNum).trim()}`] ||
      customPositions[`q-score-${idx + 1}`];

    if (customPos?.deleted) {
      return;
    }

    const mScale = customPos?.scale || 1.0;
    const mColor = customPos?.color || RED_PEN_COLOR;
    const scoreX = customPos ? customPos.xRatio * width : defaultScoreX;
    const scoreY = customPos ? customPos.yRatio * height : defaultScoreY;
    const hitRadius = 24 * scale * mScale;

    const qScoreVal =
      customPos?.score !== undefined && customPos.score !== null
        ? Number(customPos.score)
        : qs.score;

    markings.push({
      id: qScoreId,
      type: "question-score",
      markType: "question-score",
      pageNum,
      qNum: qs.qNum,
      qKey: qs.qKey,
      qIdx: qs.qIdx,
      questionIndex: qs.qIdx,
      score: qScoreVal,
      obtainedScore: qScoreVal,
      maxScore: qs.maxScore,
      questionId: qs.questionId,
      x: scoreX,
      y: scoreY,
      xRatio: +(scoreX / width).toFixed(4),
      yRatio: +(scoreY / height).toFixed(4),
      rx: 20 * scale * mScale,
      ry: 20 * scale * mScale,
      haloShape: { type: "ellipse", rx: 20 * scale * mScale, ry: 20 * scale * mScale },
      defaultX: defaultScoreX,
      defaultY: defaultScoreY,
      scale: mScale,
      color: mColor,
      hitRadius,
      label: `Q${qs.qNum} Score (${qScoreVal}/${qs.maxScore})`,
      draw: (c, isHov, isDrag, isSel) => {
        drawQuestionScoreCircle(c, {
          x: scoreX,
          y: scoreY,
          score: qScoreVal,
          seed: idx * 19 + 13,
          color: mColor,
          scale: scale * mScale,
        });
        if (options.interactive && (isHov || isDrag || isSel)) {
          drawSelectionHalo(c, scoreX, scoreY, { rx: 20 * scale * mScale, ry: 20 * scale * mScale }, isDrag, `Q${qs.qNum}: ${qScoreVal}/${qs.maxScore}`, isSel);
        }
      },
    });
  });

  // 4. Render User-Added Custom Markings
  Object.keys(customPositions).forEach((cKey) => {
    const customMark = customPositions[cKey];
    if (!customMark || typeof customMark !== "object") return;
    if (customMark.deleted) return;
    if (Number(customMark.pageNum || 1) !== pageNum) return;

    const isCustom = customMark.isCustom || cKey.startsWith("custom-");
    if (!isCustom) return;

    // Never render positional aliases as custom marks
    if (/^(?:ann-comment|ann-loop|ann-mark|step-mark)-(?:q)?\d+-(?:[as])?\d+$/.test(cKey)) return;

    // Skip if this marking ID was already rendered from result annotations or steps
    if (
      markings.some(
        (m) =>
          m.id === cKey ||
          (customMark.annotationId && (m.annotationId === customMark.annotationId || m.id === `ann-comment-${customMark.annotationId}` || m.id === `ann-loop-${customMark.annotationId}`)) ||
          (customMark.stepId && (m.stepId === customMark.stepId || m.id === `step-mark-${customMark.stepId}`)) ||
          (m.id && cKey && m.id.replace(/^ann-(?:comment|loop|mark)-/, "") === cKey.replace(/^ann-(?:comment|loop|mark)-/, ""))
      )
    ) {
      return;
    }

    markSeedCounter += 13;
    const markSeed = markSeedCounter;
    const markX = customMark.xRatio != null ? customMark.xRatio * width : width / 2;
    const markY = customMark.yRatio != null ? customMark.yRatio * height : height / 2;
    const mScale = customMark.scale || 1.0;
    const mColor = customMark.color || RED_PEN_COLOR;
    const mThickness = (customMark.strokeWidth || 2.0) * scale * mScale;
    const mType =
      customMark.type ||
      (cKey.includes("tick")
        ? "tick"
        : cKey.includes("cross")
        ? "cross"
        : cKey.includes("line")
        ? "line"
        : cKey.includes("comment")
        ? "comment"
        : "circle");

    if (mType === "tick") {
      const hitRadius = 22 * scale * mScale;
      markings.push({
        id: cKey,
        type: "custom-tick",
        markType: "step-mark",
        pageNum,
        questionIndex: customMark.questionIndex,
        questionId: customMark.questionId,
        stepIndex: customMark.stepIndex,
        stepId: customMark.stepId,
        x: markX,
        y: markY,
        scale: mScale,
        color: mColor,
        hitRadius,
        label: "Checkmark",
        draw: (c, isHov, isDrag, isSel) => {
          drawRedCheckmark(c, markX, markY, mColor, markSeed, scale * 0.95 * mScale);
          if (options.interactive && (isHov || isDrag || isSel)) {
            drawSelectionHalo(c, markX, markY, 18 * scale * mScale, isDrag, "Checkmark", isSel);
          }
        },
      });
    } else if (mType === "cross") {
      const hitRadius = 22 * scale * mScale;
      markings.push({
        id: cKey,
        type: "custom-cross",
        markType: "step-mark",
        pageNum,
        questionIndex: customMark.questionIndex,
        questionId: customMark.questionId,
        stepIndex: customMark.stepIndex,
        stepId: customMark.stepId,
        x: markX,
        y: markY,
        scale: mScale,
        color: mColor,
        hitRadius,
        label: "Crossmark",
        draw: (c, isHov, isDrag, isSel) => {
          drawRedCrossmark(c, markX, markY, mColor, markSeed, scale * 0.95 * mScale);
          if (options.interactive && (isHov || isDrag || isSel)) {
            drawSelectionHalo(c, markX, markY, 18 * scale * mScale, isDrag, "Crossmark", isSel);
          }
        },
      });
    } else if (mType === "circle" || mType === "loop") {
      const rx = (customMark.radiusRatio ? customMark.radiusRatio * width : 28 * scale) * mScale;
      const ry = (customMark.heightRatio ? (customMark.heightRatio * height) / 2 : 18 * scale) * mScale;
      const hitRadius = Math.max(rx, 24 * scale);
      const circleHaloShape = { rx: rx + 4 * scale, ry: ry + 4 * scale };
      markings.push({
        id: cKey,
        type: "custom-circle",
        markType: "ann-loop",
        pageNum,
        questionIndex: customMark.questionIndex,
        questionId: customMark.questionId,
        annotationIndex: customMark.annotationIndex,
        annotationId: customMark.annotationId,
        x: markX,
        y: markY,
        rx,
        ry,
        haloShape: circleHaloShape,
        scale: mScale,
        color: mColor,
        strokeWidth: customMark.strokeWidth || 2.0,
        hitRadius,
        label: "Circle Loop",
        draw: (c, isHov, isDrag, isSel) => {
          drawHandDrawnLoop(c, {
            cx: markX,
            cy: markY,
            rx,
            ry,
            color: mColor,
            thickness: mThickness,
            seed: markSeed,
          });
          if (options.interactive && (isHov || isDrag || isSel)) {
            drawSelectionHalo(c, markX, markY, circleHaloShape, isDrag, "Circle", isSel);
          }
        },
      });
    } else if (mType === "line") {
      const lineLen = (customMark.widthRatio ? customMark.widthRatio * width : 80 * scale) * mScale;
      const halfW = lineLen / 2;
      const lineHaloShape = {
        type: "rect",
        x: markX - halfW - 6 * scale,
        y: markY - 8 * scale,
        w: lineLen + 12 * scale,
        h: 16 * scale,
      };
      const hitTest = (hx, hy) => {
        return (
          hx >= markX - halfW - 8 * scale &&
          hx <= markX + halfW + 8 * scale &&
          hy >= markY - 14 * scale &&
          hy <= markY + 14 * scale
        );
      };
      markings.push({
        id: cKey,
        type: "custom-line",
        markType: "ann-loop",
        pageNum,
        questionIndex: customMark.questionIndex,
        questionId: customMark.questionId,
        annotationIndex: customMark.annotationIndex,
        annotationId: customMark.annotationId,
        x: markX,
        y: markY,
        boxW: lineLen,
        boxH: 16 * scale,
        haloShape: lineHaloShape,
        scale: mScale,
        color: mColor,
        strokeWidth: customMark.strokeWidth || 2.0,
        hitRadius: 20 * scale,
        hitTest,
        label: "Horizontal Line",
        draw: (c, isHov, isDrag, isSel) => {
          drawHandDrawnLine(c, {
            x1: markX - halfW,
            y1: markY,
            x2: markX + halfW,
            y2: markY,
            color: mColor,
            thickness: mThickness,
            seed: markSeed,
          });
          if (options.interactive && (isHov || isDrag || isSel)) {
            drawSelectionHalo(
              c,
              markX,
              markY,
              lineHaloShape,
              isDrag,
              "Line",
              isSel
            );
          }
        },
      });
    } else if (mType === "comment") {
      const commentText = customMark.text || "Feedback comment";
      const fontSize = customMark.fontSize || 11;
      let renderedBox = { boxW: 140 * scale, boxH: 26 * scale, x: markX, y: markY };

      const hitTest = (hx, hy) => {
        const w = renderedBox.boxW;
        const h = renderedBox.boxH;
        const curX = renderedBox.x ?? markX;
        const curY = renderedBox.y ?? markY;
        return (
          hx >= curX - 6 * scale &&
          hx <= curX + w + 6 * scale &&
          hy >= curY - 6 * scale &&
          hy <= curY + h + 6 * scale
        );
      };

      markings.push({
        id: cKey,
        type: "custom-comment",
        markType: "comment",
        pageNum,
        questionIndex: customMark.questionIndex,
        questionId: customMark.questionId,
        annotationIndex: customMark.annotationIndex,
        annotationId: customMark.annotationId,
        x: markX,
        y: markY,
        boxW: renderedBox.boxW,
        boxH: renderedBox.boxH,
        scale: mScale,
        color: mColor,
        text: commentText,
        fontSize,
        hitRadius: 26 * scale,
        hitTest,
        label: "Comment",
        draw: (c, isHov, isDrag, isSel) => {
          const res = drawCustomCommentBox(c, {
            x: markX,
            y: markY,
            text: commentText,
            color: mColor,
            fontSize,
            scale: scale * mScale,
            fontFamily: customMark.fontFamily === "handwritten" ? "handwritten" : "sans",
            showPointer: customMark.showPointer === true,
            pointerTargetX: customMark.targetX != null ? customMark.targetX * width : null,
            pointerTargetY: customMark.targetY != null ? customMark.targetY * height : null,
            canvasWidth: width,
            canvasHeight: height,
          });
          if (res) renderedBox = res;

          const haloX = renderedBox.x ?? markX;
          const haloY = renderedBox.y ?? markY;

          if (options.interactive && (isHov || isDrag || isSel)) {
            drawSelectionHalo(
              c,
              haloX + renderedBox.boxW / 2,
              haloY + renderedBox.boxH / 2,
              {
                type: "rect",
                x: haloX - 4 * scale,
                y: haloY - 4 * scale,
                w: renderedBox.boxW + 8 * scale,
                h: renderedBox.boxH + 8 * scale,
              },
              isDrag,
              "Comment",
              isSel
            );
          }
        },
      });
    }
  });

  // Render each marking to canvas
  markings.forEach((mark) => {
    const isHovered = options.hoveredMarkId === mark.id;
    const isDragging = options.draggingMarkId === mark.id;
    const isSelected = options.selectedMarkId === mark.id;
    mark.draw(ctx, isHovered, isDragging, isSelected);
  });

  return markings;
}
